import { KnowledgeBaseService } from '../../../modules/olp-api/services/kb/kb.service';
import { Router } from '@angular/router';
import {
  EventEmitter,
  Inject,
  Injectable,
  OnDestroy,
  DOCUMENT,
} from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  Observable,
  catchError,
  map,
  Subscription,
  take,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  Activity,
  Population,
  LearningTarget,
  TextData,
  VideoData,
  TargetResultData,
  SurveyPrompt,
  ImageData,
  NextUp,
  AnswerChoice,
  PopulationMultipleChoice,
  PopulationDragAndDrop,
} from '../../interfaces';
import {
  ActivityStateEnum,
  AnswerStateEnum,
  ActivityTool,
  LanguageCodes,
  SaveIntervalTypes,
  NonMutableTryTypes,
  TemplateID,
  SaveAnswerStatesTypes,
  MessageCodes,
  MultipleChoiceTypes,
  SortingBucketTypes,
} from './../../enums';
import {
  ProgressBarOrientationEnum,
  ProgressNodeFocusEnum,
  ProgressNodeStatusEnum,
  ProgressNodeTypeEnum,
} from 'src/app/modules/progress-bar/enums';
import {
  EXIT_SURVEY_TYPES,
  exitSurveys,
  sandboxActivities,
  siNoChoices,
  yesNoChoices,
  yesNoicons,
} from 'src/app/data';
import { IProgressBarConfig } from 'src/app/modules/progress-bar/interfaces/progress-bar-config.interface';
import { IProgressNodeData } from 'src/app/modules/progress-bar/interfaces/progress-node-data.interface';
import { PageProgressService } from '../page-progress-bar/page-progress.service';
import { videos } from 'src/app/data';
import { VideoDefaultConfig } from 'src/app/modules/video/data';
import { VideoService } from 'src/app/modules/video/services/video.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivityObjectTypes } from '../../enums/activity-object-types';
import { ActivityManifestObject } from '../../interfaces/activity-manifest-object';
import { ToolsService } from 'src/app/modules/tools/services/tools.service';
import { TimeElapsedService } from 'src/app/shared/services/time-elapsed/time-elapsed.service';
import { FeatureFlagService } from '../../../modules/feature-flags/services/feature-flag/feature-flag.service';
import { ResultsService } from '../results/results.service';
import { UserService } from '../user/user.service';
import { PopulationService } from '../population/population.service';
import { FeatureFlags } from '../../enums/feature-flags';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';
import { SpeechSynthesisService } from 'src/app/modules/speech-synthesis/services/speech-synthesis/speech-synthesis.service';
import { InactivityService } from '../inactivity/inactivity.service';
import { TrackingService } from 'src/app/modules/tracking/services/tracking.service';
import { ISlideData } from 'src/app/modules/carousel/interfaces';
import { VideoSaveStateService } from 'src/app/modules/video/services/video-save-state.service';
import {
  CompleteActivityState,
  IncompleteActivityState,
} from 'src/app/modules/activity-save-state';
import { UseAnimationService } from 'src/app/modules/use-animation/use-animation.service';

import { FooterService } from 'src/app/modules/footer/services/footer.service';
import { AuthSessionService } from 'src/app/modules/auth/services/auth-session/auth-session.service';
import seedrandom from 'seedrandom';
import { footerData } from 'src/app/data/footer-data';
import { ClickstreamService } from 'src/app/modules/olp-api/services/clickstream/clickstream.service';
import { ClickstreamInteractionEventType } from 'src/app/modules/olp-api/enums/clickstream-events';
import { NonAssessableTypes } from 'src/app/shared/enums';
import { ReferrerService } from '../referrer/referrer.service';
import { AssignmentService } from 'src/app/modules/olp-api/services/assignment/assignment.service';
import {
  LoadingScreenService,
  LoadingScreenState,
} from 'src/app/modules/loading-screen/services/loading-screen.service';
import { ConstructClickstreamService } from '../construct-clickstream/construct-clickstream.service';
import { ActivityStateManagerService } from 'src/app/modules/activity-save-state/services/activity-state-manager/activity-state-manager.service';
import { StudentInboxService } from 'src/app/modules/student-inbox-api/services/student-inbox.service';
import { InboxUpdateInput } from 'src/app/modules/student-inbox-api/types/student-inbox.types';
import { InboxDisplayStatus } from 'src/app/modules/student-inbox-api/enums/student-inbox.enums';
import {
  AudioDataLoadStatus,
  VerbalAudioType,
  VerbalService,
} from 'src/app/modules/read-aloud/services/verbal.service';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { HeaderConfig } from '../../types/header';
import { LoggingService } from '../logging/logging.service';
import { Feedback } from '../../components/feedback/feedback.interface';
import { PopulationData } from '../../types/population-data';

@Injectable({
  providedIn: 'root',
})
export class ActivityService implements OnDestroy {
  static readonly PAGE_BAR_LOCAL_STORAGE = 'PageProgressBar';
  static readonly SAVE_ON_STATE_TYPES = [
    ActivityObjectTypes.VIDEO,
    ActivityObjectTypes.QUESTION,
    ActivityObjectTypes.SUCCESS_CRITERIA,
  ];

  skillName$ = new BehaviorSubject<string>('');
  domain = new BehaviorSubject<string>('');
  standard = new Subject<TextData>();
  seriesName = new BehaviorSubject<string>('');
  episodeTitle = new BehaviorSubject<string>('');
  episodeTitleVerbal = new BehaviorSubject<string>('');
  episodeNumber = new BehaviorSubject<string>('0');
  learningTarget = new BehaviorSubject<LearningTarget[]>([]);
  entrySurvey$ = new BehaviorSubject<SurveyPrompt[] | undefined>(undefined);
  exitSurvey$ = new BehaviorSubject<SurveyPrompt[] | undefined>(undefined);
  video = new Subject<VideoData[]>();
  tools = new Subject<ActivityTool[]>();
  nextUp$ = new BehaviorSubject<NextUp | undefined>(undefined);
  isSandboxActivity$ = new BehaviorSubject<boolean>(false);

  isAssignment = false;
  currActivity!: Activity;
  currActivityManifest = new BehaviorSubject<ActivityManifestObject[]>([]);
  currentActivityOid = -1;
  nextActivityOid = -1;
  assignmentId: string | undefined;
  private isInboxAssignment = false;
  inboxActivityId = new BehaviorSubject<string | undefined>(undefined);
  inboxAssignmentId: string | undefined;

  showHeader = new BehaviorSubject<boolean>(false);
  showHeaderTitle = new BehaviorSubject<boolean>(true);
  showRewards = new BehaviorSubject<boolean>(false);
  showProgressBar = new BehaviorSubject<boolean>(true);
  showExitButton = new BehaviorSubject<boolean>(true);
  showElapsedTime = new BehaviorSubject<boolean>(true);
  showSecondaryHeader = new BehaviorSubject<boolean>(false);
  pageRoute = new BehaviorSubject<string>('/');

  showInternal = new BehaviorSubject<boolean>(false);
  showVersion = new BehaviorSubject<boolean>(false);

  videoData$ = new BehaviorSubject<VideoData | undefined>(videos[0]);
  videoConfig$ = new BehaviorSubject<VideoDefaultConfig | undefined>(
    new VideoDefaultConfig()
  );

  activityState = ActivityStateEnum.INVALID;
  activityStateFromKB: undefined | IncompleteActivityState; // used to store the activity state when resuming an activity
  questionNumber = -1;
  currStep = 0;
  prevStep = 0;
  shouldRoute = true;
  elapsedTime = 0;
  lastReportedInterface = '';
  advanceActivityEvent = new EventEmitter<void>();

  videoProgression = 0;
  quickExit = false;

  currentPage = 0;
  pageProgressConfig: IProgressBarConfig = {
    orientation: ProgressBarOrientationEnum.horizontal,
    useDinkus: false,
    autoDinkus: true,
    autoDinkusWindowDim: 800,
    autoDinkusNodeDim: 56,
    dinkusChildCount: 11,
    autoDinkusMinChildren: 5,
    autoDinkusMaxChildren: 10,
    autoDinkusIdealChildren: 11,
  };

  seedrandom = seedrandom();
  activitySeed = '';
  subscriptions = new Subscription();

  entrySurveyIndex = new BehaviorSubject<number>(0);
  readonly languageCode = LanguageCodes.ENGLISH as string; // default language is English

  startScreenBG_left = new BehaviorSubject<ImageData>({ id: '', url: '' });
  startScreenBG_right = new BehaviorSubject<ImageData>({ id: '', url: '' });
  startScreenFG = new BehaviorSubject<ImageData>({ id: '', url: '' });
  saveInterval!: NodeJS.Timeout;
  readonly saveIntervalTime = 5000;

  clickedOnProgNodeFlag = false;
  popAnimationInStarted = new EventEmitter<void>();

  activityDisplayStatus: InboxDisplayStatus = InboxDisplayStatus.LAUNCHED; // default display status is LAUNCHED - set in SE
  activitySubmitInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private pageProgressService: PageProgressService,
    private httpClient: HttpClient,
    private router: Router,
    private videoService: VideoService,
    private toolsService: ToolsService,
    public translate: TranslateService,
    private configService: ConfigurationService,
    private timeElapsedService: TimeElapsedService,
    private featureFlagService: FeatureFlagService,
    private kbService: KnowledgeBaseService,
    private userService: UserService,
    private resultsService: ResultsService,
    private activityStateService: ActivityStateManagerService,
    private populationService: PopulationService,
    private speechSynthesisService: SpeechSynthesisService,
    private inactivityService: InactivityService,
    private trackingService: TrackingService,
    private videoSaveStateService: VideoSaveStateService,
    private useAnimationService: UseAnimationService,
    private footerService: FooterService,
    private authSessionService: AuthSessionService,
    private clickstreamService: ClickstreamService,
    private referrerService: ReferrerService,
    private assignmentService: AssignmentService,
    private loadingScreenService: LoadingScreenService,
    private constructClickstreamService: ConstructClickstreamService,
    private studentInboxService: StudentInboxService,
    private verbalService: VerbalService,
    private messageService: MessageService,
    private loggingService: LoggingService,
    @Inject(DOCUMENT) private document: Document
  ) {
    translate.addLangs(Object.values(LanguageCodes));
    translate.setDefaultLang(this.languageCode);

    this.exitSurvey$.subscribe(survey => {
      if (survey) {
        for (let i = 0; i < survey.length; i++) {
          if (survey[i].response) {
            this.trackingService.push({
              event: 'Post Survey Response',
              response: survey[i].response,
            });
            this.trackingService.push({
              response: undefined,
            });
          }
        }
      }
    });

    this.inactivityService.inactivityEvent$.subscribe(
      (inactiveTime: number) => {
        this.clickstreamService.recordClickstreamInteraction(
          ClickstreamInteractionEventType.Inactivity,
          this.timeElapsedService.getTimeAsSeconds(),
          this.currActivityManifest.getValue()?.at(this.currStep)?.id ?? '',
          { inactiveTime: inactiveTime / 1000 }
        );
      }
    );

    this.assignmentService.isAssignment.subscribe(isAssignment => {
      this.setIsAssignment(isAssignment);
    });

    this.inboxActivityId.subscribe(inboxId => {
      this.setIsInboxAssignment(inboxId !== undefined);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setIsAssignment(value: boolean): void {
    this.isAssignment = value;
  }

  setIsInboxAssignment(value: boolean): void {
    this.isInboxAssignment = value;
    this.isAssignment = value;
  }

  getIsInboxAssignment(): boolean {
    return this.isInboxAssignment;
  }

  //#region fetch and parse activity

  /**
   * Get Activity through HTTP request with the supplied activityId and parse it when retrieved.
   * @param activityId string activityId to create the request for
   * @returns Observable which will execute the fetch when subscribed to.
   */
  fetchActivity(activityId: string): Observable<unknown> {
    let url = `api/activities/${activityId}`;
    if (
      this.featureFlagService.isFlagEnabled(FeatureFlags.CDN_ACTIVITIES) &&
      !sandboxActivities.find(activity => activity.id === activityId)
    ) {
      url = new URL(
        `PracticeData/${activityId}.json`,
        this.configService.config.assetCdnDomain
      ).toString();
    }

    this.loggingService.activityUuid.next(activityId);
    this.loggingService.logInfo('fetch_activity', {
      url: url,
      displayStatus: this.activityDisplayStatus,
    });

    return this.httpClient.get<Activity>(url).pipe(
      catchError(err => {
        this.loggingService.logInfo('fetch_activity_failed', {
          url: url,
          error: err.message,
          status: err.status,
          displayStatus: this.activityDisplayStatus,
        });
        console.error('Error fetching activity', err);
        throw err;
      }),
      map(res => {
        //Keeping in this console log for testing in dev purposes, will remove later. (3/4/24 BL)
        console.log(`we have a return from url [${url}] of`, res);
        const filteredActivity = this.filterActivityManifestBasedOnGrade(res);
        this.currActivity = this.parseTokensInPopulations(filteredActivity);
        if (
          this.featureFlagService.isFlagEnabled(
            FeatureFlags.ENABLE_STUDENT_INBOX
          )
        ) {
          this.currActivity.inboxActivityId =
            this.inboxActivityId.getValue() ?? '';
        }

        this.parseActivity();
      })
    );
  }

  /**
   * Get activity through Http request with supplied activityId, but don't parse the activity.
   * @param activityId string activityId to create the request for
   * @returns Observable which will execute the fetch when subscribed to.
   */
  fetchActivityInfo(activityId: string): Observable<Activity> {
    const url = `api/activities/${activityId}`;
    return this.httpClient.get<Activity>(url).pipe(
      catchError(err => {
        console.error('Error fetching activity', err);
        throw err;
      })
    );
  }

  /**
   * Parse the current activity, and populate the subjects.
   */
  parseActivity(): void {
    console.log("We're about to parse the activity.", this.currActivity);

    this.loggingService.logInfo('activity_parse', {
      activityData: this.currActivity,
      displayStatus: this.activityDisplayStatus,
    });

    try {
      // Verbal pause/resume is handled in TeiPage; avoid stale toolbar state
      // when Tei is destroyed (e.g. video/repeat-prompt resume with no TEI).
      this.populationService.setDisableToolbar(false);
      this.verbalService.stopCurrent();
      this.prevStep = 0; //PRE_START_SCREEN now makes this start at -1
      this.parseStandard()
        .parseDomain()
        .parseAvailableTools()
        .parseSeriesName()
        .parseSkillName()
        .parseActivityEpisode()
        .parseNextUp()
        .parseVideo();

      this.setLanguage();
      this.parseLearningTarget();
      this.parseActivityManifest();
      this.createEntryScreenSurvey();

      this.processGlobalRetryOverride();
      // Drop prior-question context so toolbar/read-aloud do not use the last
      // activity's population until the next TEI step runs.
      this.populationService.currentPopulation.next(undefined);
      this.currActivityManifest.next(this.currActivity.activityManifest);

      this.resultsService.numQuestions = this.currActivity.populations.length;
      this.populationService.populations = this.currActivity.populations;
      this.populationService.populationResponses = new Array(
        this.populationService.populations.length
      )
        .fill({})
        .map(() => {
          return {
            responses: [],
            elapsedTime: 0,
          };
        });
    } catch (error) {
      console.error('Error parsing activity', error);
    }
  }
  //#endregion

  randomizeAnswerChoices(): void {
    let answerChoices: AnswerChoice[];

    if (!this.activitySeed) {
      this.activitySeed = this.seedrandom.double().toString();
      this.seedrandom = seedrandom(this.activitySeed);
    }

    this.currActivity.populations.forEach(population => {
      if (
        !this.featureFlagService.isFlagEnabled(
          FeatureFlags.RANDOMIZE_ANSWER_CHOICES
        )
      ) {
        return;
      }

      if ('answerChoices' in population.populationData) {
        answerChoices = population.populationData.answerChoices ?? [];
      }

      if (!answerChoices) {
        return;
      }

      if (
        population.templateID === TemplateID.INLINE_CHOICE ||
        population.templateID === TemplateID.STATIC_HORIZONTAL_SEQUENCING ||
        population.templateID === TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING ||
        population.templateID === TemplateID.STATIC_VERTICAL_SEQUENCING
      ) {
        answerChoices.forEach(answerChoice => {
          this.shuffle(answerChoice.choice.texts ?? []);
          this.shuffle(answerChoice.choice.images ?? []);
          this.shuffle(answerChoice.choice.videos ?? []);
          this.shuffle(answerChoice.choice.audios ?? []);
        });
      } else {
        this.shuffle(answerChoices);
      }
      answerChoices = [];
    });

    console.log('Current seed: ', this.activitySeed);
  }

  shuffle<Type>(array: Type[]): Type[] {
    let currentIndex = array.length,
      randomIndex;

    while (currentIndex != 0) {
      randomIndex = Math.floor(this.seedrandom() * currentIndex--);
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }

    return array;
  }

  launchActivity(id: string, oid?: number): Promise<boolean> {
    this.resetActivityState();
    return new Promise<boolean>((resolve, reject) => {
      this.fetchActivity(id).subscribe({
        next: () => {
          this.timeElapsedService.startTimer();
          if (oid) {
            this.currentActivityOid = oid;
          }
          this.initActivity();
          resolve(true);
        },
        error: () => {
          reject(false);
          return this.resetActivityAndNavigateHome();
        },
      });
    });
  }

  private processGlobalRetryOverride(): void {
    if (
      !this.currActivity ||
      this.currActivity.globalTryOverride === undefined
    ) {
      return;
    }
    this.currActivity.populations.forEach(pop => {
      if (
        pop.populationData.tries === undefined &&
        !NonMutableTryTypes.includes(pop.templateID)
      ) {
        pop.populationData.tries = this.currActivity.globalTryOverride;
      }
    });
  }

  private parseLearningTarget(): this {
    if (this.currActivity.startScreenData?.learningTarget) {
      // remove any success criteria that are not associated with the current populations
      this.currActivity.startScreenData.learningTarget.forEach(lt => {
        lt.successCriteria = lt.successCriteria.filter(successCriteria => {
          return (
            this.currActivity.populations.findIndex(pop => {
              return (
                pop.populationData.associatedSuccessCriteria?.findIndex(sc => {
                  return sc === successCriteria.id;
                }) !== -1
              );
            }) !== -1
          );
        });
      });

      this.learningTarget.next(
        this.currActivity.startScreenData.learningTarget
      );
    }
    return this;
  }

  private parseStandard(): this {
    if (this.currActivity.standard) {
      this.standard.next(this.currActivity.standard);
    }

    return this;
  }

  private parseDomain(): this {
    if (this.currActivity.domain?.text) {
      this.domain.next(this.currActivity.domain.text);
    }

    return this;
  }

  private parseAvailableTools(): this {
    if (this.currActivity.availableTools) {
      this.tools.next(this.currActivity.availableTools);
    }

    return this;
  }

  private parseSeriesName(): this {
    if (this.currActivity.seriesName) {
      this.seriesName.next(this.currActivity.seriesName);
    }

    return this;
  }

  private parseSkillName(): this {
    if (this.currActivity.skillName) {
      this.skillName$.next(this.currActivity.skillName);
    }

    return this;
  }

  private parseNextUp(): this {
    if (this.currActivity.nextUp) {
      this.nextUp$.next(this.currActivity.nextUp);
    } else {
      this.nextUp$.next(undefined);
    }

    return this;
  }

  private parseActivityEpisode(): this {
    if (this.currActivity.episodeTitle) {
      this.episodeTitle.next(this.currActivity.episodeTitle);
    }

    if (this.currActivity.episodeTitleVerbal) {
      this.episodeTitleVerbal.next(this.currActivity.episodeTitleVerbal);
    }

    if (this.currActivity.episodeNumber) {
      this.episodeNumber.next(this.currActivity.episodeNumber);
    }

    return this;
  }

  private parseVideo(): this {
    const video = this.currActivity.videos[0];
    if (video) {
      if (this.featureFlagService.isFlagEnabled(FeatureFlags.CDN_ASSETS)) {
        video.url = new URL(
          video.url,
          this.configService.config.assetCdnDomain
        ).toString();
        video.type = 'application/x-mpegurl';
        video.lang = this.currActivity.language;
        video.captions?.forEach(caption => {
          if (!caption.src) return;
          caption.src = new URL(
            caption.src,
            this.configService.config.assetCdnDomain
          ).toString();
        });
        if (video.posterURL) {
          video.posterURL = new URL(
            video.posterURL,
            this.configService.config.assetCdnDomain
          ).toString();
        }
      }
      this.videoData$.next(video as VideoData);
    }
    return this;
  }

  private setLanguage(): void {
    const language = this.currActivity.language || 'en';
    this.document.documentElement.lang = language;
    this.translate.use(language);
    this.speechSynthesisService.lang =
      this.currActivity.language === LanguageCodes.ENGLISH
        ? LanguageCodes.ENGLISH_EXTENDED
        : LanguageCodes.SPANISH_EXTENDED;
  }

  private createEntryScreenSurvey(): void {
    const surveys: SurveyPrompt[] = [];
    if (this.learningTarget.getValue()) {
      this.learningTarget.getValue().forEach(target => {
        target.successCriteria.forEach(criteria => {
          surveys.push({
            prompt: criteria,
            choices:
              this.currActivity.language === LanguageCodes.ENGLISH
                ? yesNoChoices
                : siNoChoices,
            icons: yesNoicons,
          });
        });
      });
      this.entrySurvey$.next(surveys);
    }
  }

  private parseActivityManifest(): void {
    for (const piece of this.currActivity.activityManifest) {
      switch (piece.type) {
        case ActivityObjectTypes.ENTRY_SURVEY: {
          if (this.currActivity.entrySurvey) {
            this.entrySurvey$.next(this.currActivity.entrySurvey);
          }
          break;
        }
        case ActivityObjectTypes.START_SCREEN: {
          this.parseActivityStartScreenManifest();
          break;
        }

        case ActivityObjectTypes.EXIT_SURVEY: {
          if (this.currActivity.language === LanguageCodes.ENGLISH) {
            this.exitSurvey$.next([exitSurveys[EXIT_SURVEY_TYPES.ENGLISH]]);
          } else {
            this.exitSurvey$.next([exitSurveys[EXIT_SURVEY_TYPES.SPANISH]]);
          }
          break;
        }
        case ActivityObjectTypes.RESULTS_SCREEN: {
          //does nothing for now
          break;
        }
      }
    }
  }

  private parseActivityStartScreenManifest(): void {
    if (this.currActivity.startScreenData) {
      if (this.currActivity.startScreenData.background_leftImage) {
        this.startScreenBG_left.next(
          this.currActivity.startScreenData.background_leftImage
        );
      }

      if (this.currActivity.startScreenData.background_rightImage) {
        this.startScreenBG_right.next(
          this.currActivity.startScreenData.background_rightImage
        );
      }

      if (this.currActivity.startScreenData.foreground) {
        this.startScreenFG.next(this.currActivity.startScreenData.foreground);
      }
    }
  }

  /**
   * Get the data that the ActivityManifestObject is referring to
   * @param obj ActivityManifestObject to get the asset data from
   * @returns filled data from the manifest object, or undefined if not found
   */
  getAssetData(
    obj: ActivityManifestObject
  ): VideoData | Population | undefined {
    switch (obj.type) {
      case ActivityObjectTypes.VIDEO: {
        if (!obj.id) {
          break;
        }
        const video = this.currActivity.videos.find(
          element => element.id === obj.id
        );
        if (video) {
          return video;
        } else {
          break;
        }
      }
      case ActivityObjectTypes.QUESTION: {
        if (!obj.id) break;
        const population = this.currActivity.populations.find(
          element => element.id === obj.id
        );
        if (population) {
          return population;
        } else {
          break;
        }
      }
    }
    return undefined;
  }

  /**
   * Initialize a brand new activity based on currentActivity if activityId is undefined, attempt to initialize with saved state if string is provided
   * @param activityId undefined to init new activity; string activityId to check local storage to init with saved activity state
   */
  initActivity(activityId?: string): void {
    if (activityId) {
      this.activityStateService
        .getIncompleteActivityState(activityId)
        .then(activity => {
          if (activity) {
            // resuming existing activity
            console.log(
              'activityState after resume for',
              activityId,
              'is',
              activity
            );
            // Always route to Pre-start / start screen when resuming an activity based on grades for all microlessons.
            this.activityStateFromKB = activity;
            if (this.isPreK2ReadingActivity()) {
              this.activityState = ActivityStateEnum.PRE_START_SCREEN;
            } else {
              this.activityState = ActivityStateEnum.START;
            }
            let nextStep = this.activityStateFromKB.currentStep + 1;
            // If next step is success criteria, then we need to skip it and check for the next step as we never resume from SC page.
            if (
              this.currActivity.activityManifest[nextStep]?.type ===
              ActivityObjectTypes.SUCCESS_CRITERIA
            ) {
              nextStep++;
            }
            if (
              this.currActivity.activityManifest[nextStep]?.type ===
              ActivityObjectTypes.QUESTION
            ) {
              if (
                this.checkIfCurrentQuestionisComplete(
                  this.currActivity.activityManifest[nextStep].id as string,
                  this.activityStateFromKB
                )
              ) {
                nextStep++;
              }
              // load the next audio data population
              this.loadAudioDataForNextPopulation(
                this.getAssetData(
                  this.currActivity.activityManifest[nextStep]
                ) as Population
              );
            }
            this.setHeaderAndRoute();
            // to remove the loading animation when resuming an activity from student inbox
            this.loadingScreenService.loadingScreenState.set(
              LoadingScreenState.animatingOut
            );
          } else {
            this.initNewActivity();
          }
        })
        .catch(() => {
          this.initNewActivity();
        });
    } else {
      this.initNewActivity();
    }
  }

  resumeActivity(): void {
    try {
      this.resumeActivityFromState(
        this.activityStateFromKB as IncompleteActivityState
      );
      this.randomizeAnswerChoices();
      this.activityStateFromKB = undefined;
    } catch (err) {
      console.error('Error resuming activity', err);
    }
  }

  private initNewActivity(): void {
    this.activityStateFromKB = undefined;
    this.verbalService.stopCurrent();
    this.populationService.setDisableToolbar(false);
    this.activitySubmitInProgress$.next(false);
    this.currStep = -1;
    this.questionNumber = -1;
    this.populationService.answerStates = new Array<AnswerStateEnum>(
      this.populationService.populations.length
    ).fill(AnswerStateEnum.INCOMPLETE);
    this.inactivityService.endInactivityTracking();
    this.inactivityService.startInactivityTracking();
    this.videoSaveStateService.getVideoSaveStateWithId(
      this.currActivity.videos[0].id
    );
    this.randomizeAnswerChoices();
    this.reportCurrentActivityToAnalytics();
    this.startActivity();

    // Need to keep this logic here, because an activity can have any order of the nodes.
    // For example,
    //    question, video, video, question
    //    video, question, question
    //    etc.
    // So we don't want to create a self-selected assignment on the video node, if the
    // video node comes after the question node.
    // This is the most logical place to create/update an assignment
    if (
      this.featureFlagService.isFlagEnabled(FeatureFlags.USE_ASSIGNMENT_API) &&
      this.isAssignment &&
      !this.authSessionService.isTeacher
    ) {
      this.assignmentService.checkForAccessToken();
      this.handleAssignmentRecord(this.currentActivityOid);
    }

    if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_CLICKSTREAM)) {
      this.clickstreamService.checkForAccessToken();
      this.clickstreamService.clearClickstreamInteractionLog();
      this.clickstreamService.clickstream.initStartTime(Date.now());
      this.clickstreamService.clickstream.initSchoolYear(
        this.userService.schoolYear
      );
    }

    this.loadingScreenService.loadingScreenState.set(
      LoadingScreenState.animatingOut
    );
  }

  // eslint-disable-next-line complexity
  resumeActivityFromState(activityState: IncompleteActivityState): void {
    if (
      !this.featureFlagService.isFlagEnabled(
        FeatureFlags.ENABLE_STUDENT_INBOX
      ) &&
      this.featureFlagService.isFlagEnabled(FeatureFlags.USE_ASSIGNMENT_API) &&
      !this.authSessionService.isTeacher
    ) {
      this.assignmentService.checkForAccessToken();
      this.handleAssignmentRecord(this.currentActivityOid);
    }

    if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_CLICKSTREAM)) {
      this.clickstreamService.checkForAccessToken();
      this.clickstreamService.setClickstreamInteractions(
        activityState.clickstreamInteractions
      );
      this.clickstreamService.clickstream.initStartTime(
        new Date(activityState.startTime).getTime()
      );
      this.clickstreamService.clickstream.initSchoolYear(
        this.userService.schoolYear
      );
    }

    let nextStep =
      this.currActivity.activityManifest.length > activityState.currentStep + 1
        ? activityState.currentStep + 1
        : activityState.currentStep;

    this.setupActivityFromState(activityState);
    this.startPageProgressBar(activityState.progressNodes, 1);
    this.inactivityService.endInactivityTracking();
    this.inactivityService.startInactivityTracking();
    this.reportCurrentActivityToAnalytics();

    const currActivityManifest = this.currActivity.activityManifest[nextStep];
    if (currActivityManifest?.type === ActivityObjectTypes.SUCCESS_CRITERIA) {
      nextStep++; // move to the TEIs when trying to resume from success criteria
    }
    const { id, type } = this.currActivity.activityManifest[nextStep];
    if (
      type === ActivityObjectTypes.QUESTION ||
      type === ActivityObjectTypes.VIDEO
    ) {
      this.handleProgressBarResume(activityState, id ?? '', type, nextStep);
      return; // to skip animatingOut state for question and vido resume flows.
    } else if (
      this.featureFlagService.isFlagEnabled(FeatureFlags.SAVE_ACTIVITY_ANYWHERE)
    ) {
      this.advanceActivity(true);
    }

    this.loadingScreenService.loadingScreenState.set(
      LoadingScreenState.animatingOut
    );
  }

  async handleAssignmentRecord(currentActivityOid: number): Promise<void> {
    this.assignmentService.assignmentIdFromApi.subscribe(id => {
      // need to assign assignmentId to the current activity.
      // If we skip this part, then we unable to update the assignment record
      // because we don't have the assignment ID when we are redirected from SWE.
      this.assignmentId = id.toString();
    });
    // for sure assignment id will be a number cause assignmentIdFromApi.subscribe always returns number
    const id = Number(this.assignmentId);

    console.log(
      'Assignment Record: assigned Id and current Oid',
      id,
      currentActivityOid
    );

    // will prioritize finding assignment via id and fallback to self-selected by contentId
    // if still cannot find record will create a new self-selected one
    const res = await this.assignmentService.createOrFindActivityRecord(
      id,
      currentActivityOid
    );
    if (!res) {
      console.warn(
        'Warning! Cannot find or create a new assignment record for id and contentId of:',
        id,
        currentActivityOid
      );
    }
  }

  checkIfCurrentQuestionisComplete(
    qId: string,
    activityState: IncompleteActivityState
  ): boolean {
    const index = this.currActivity.activityManifest
      .filter(a => a.type === ActivityObjectTypes.QUESTION)
      .findIndex(q => q.id === qId);

    if (index !== -1) {
      const isFinishedQuestion =
        activityState.answerStates[index] !== AnswerStateEnum.INCOMPLETE;

      // advance to next question if finished and not in teacher mode
      if (isFinishedQuestion && !this.authSessionService.isTeacher) {
        activityState.selectedAnswers = [[]];
        return true; // question is complete
      }
    }
    return false; // question is not complete
  }

  handleProgressBarResume(
    activityState: IncompleteActivityState,
    id: string,
    type: string,
    nextStep: number
  ): void {
    if (!id) {
      this.advanceActivity(true);
      return;
    }

    if (type === ActivityObjectTypes.QUESTION) {
      if (this.checkIfCurrentQuestionisComplete(id, activityState)) {
        nextStep++;
      }

      this.resumeOrAdvanceActivity(
        nextStep,
        activityState.curHintsRevealed,
        activityState.selectedAnswers
      );
    } else if (type === ActivityObjectTypes.VIDEO) {
      this.processVideoState(nextStep)
        .then(() => {
          if (
            this.videoSaveStateService.curVideoState$.getValue().videoWatched &&
            !this.authSessionService.isTeacher &&
            this.currActivityManifest.value[nextStep].id ===
              this.videoSaveStateService.curVideoState$.value.id
          ) {
            nextStep++;
          }
          // If videoWatched is true AND if user is a PK2 user AND if the next step is success criteria, then resume from TEIs
          if (
            this.videoSaveStateService.curVideoState$.getValue().videoWatched &&
            this.currActivityManifest.value[nextStep].type ===
              ActivityObjectTypes.SUCCESS_CRITERIA
          ) {
            nextStep++;
          }

          this.resumeOrAdvanceActivity(
            nextStep,
            activityState.curHintsRevealed,
            activityState.selectedAnswers
          );
        })
        .catch(() => console.log('Error processing video save state data'));
    } else {
      this.resumeOrAdvanceActivity(
        nextStep,
        activityState.curHintsRevealed,
        activityState.selectedAnswers
      );
    }
  }

  resumeOrAdvanceActivity(
    nextStep: number,
    curHintsRevealed?: ISlideData[],
    selectedAnswers?: (string | number)[][]
  ): void {
    const progStartIndex = this.findStartProgressBarStep();
    const nodeIndex = nextStep - progStartIndex;
    if (
      nodeIndex >= 0 &&
      nodeIndex <
        this.currActivity.activityManifest.filter(
          a =>
            a.type === ActivityObjectTypes.QUESTION ||
            a.type === ActivityObjectTypes.VIDEO
        ).length
    ) {
      this.goToProgressNodeStep(
        this.pageProgressService.AvailableProgressNodes[nodeIndex],
        curHintsRevealed,
        selectedAnswers
      );
      if (selectedAnswers) {
        this.populationService.lastFilledCheck = this.populationService.currTry;
        this.populationService.setSelectedAnswers(selectedAnswers);
      }
    } else {
      this.advanceActivity(true);
    }
  }

  handleResumePopulation(activity: IncompleteActivityState): void {
    this.populationService.currTry = activity.currTry ?? 0;
    this.populationService.selectedAnswers.next(activity.selectedAnswers ?? []);
    this.populationService.filledAnswers.next(activity.filledAnswers ?? []);
    this.populationService.disabledAnswers.next(
      this.populationService.getCurrentPopAnswerState() ===
        AnswerStateEnum.COMPLETE
        ? [['disabled']]
        : activity.disabledAnswers ?? []
    );
    this.populationService.prevSelectedAnswers =
      this.populationService.selectedAnswers.getValue();
    this.questionNumber = activity.questionNumber;
    const popIndex = this.currActivity.activityManifest
      .filter(a => a.type === ActivityObjectTypes.QUESTION)
      .findIndex(
        q => q.id === this.currActivity.activityManifest[this.currStep].id
      );
    if (popIndex !== -1) {
      this.populationService.currentPopulation.next(
        this.populationService.populations[popIndex]
      );
    }

    this.populationService.resumeActivity();

    this.toolsService.deactivateAllTools();

    // refresh the tei to show updated info
    const curPop = this.populationService.currentPopulation.getValue();
    if (curPop) {
      this.setSeenHints(activity);
    }
  }

  setSeenHints(activity: IncompleteActivityState): void {
    // handle seen hints
    // Could probably have edited obj directly but seemed sketchy
    const popVal = this.populationService.currentPopulation.getValue();

    if (popVal && popVal.hint) {
      const newHints: ISlideData[] = [];

      popVal.hint.forEach(hint => {
        const newHint = { ...hint };

        if (
          activity.curHintsRevealed?.find((h: ISlideData) => h.id === hint.id)
        ) {
          newHint.wasViewed = true;
        }

        newHints.push(newHint);
      });
      this.populationService.currentPopulation.next({
        ...popVal,
        hint: newHints,
      });
      if (newHints.every(hint => hint.wasViewed)) {
        this.populationService.isAllHintsSeen.next(true);
      }
    }
  }

  setIsSandboxActivity(value: boolean): void {
    this.isSandboxActivity$.next(value);
  }

  /**
   * Sets the service's state to a passed in state.
   * @param state IncompleteActivityState to setup service with
   */
  setupActivityFromState(state: IncompleteActivityState): void {
    this.currStep = state.currentStep;
    this.questionNumber = state.questionNumber;
    this.resultsService.numPerfect = state.numPerfect;
    this.resultsService.numCorrect = state.numCorrect;
    this.resultsService.numIncorrect = state.numIncorrect;
    this.populationService.currTry = state.currTry ?? 0;
    this.populationService.answerStates = state.answerStates;
    for (let i = 0; i < state.populationResponses.length; i++) {
      this.populationService.populationResponses[i] =
        state.populationResponses[i];
    }
    this.populationService.selectedAnswers.next(state.selectedAnswers ?? []);
    this.populationService.disabledAnswers.next(state.disabledAnswers ?? []);
    this.populationService.prevSelectedAnswers =
      this.populationService.selectedAnswers.getValue();
    if (
      this.questionNumber >= 0 &&
      this.questionNumber < this.currActivity.populations.length
    ) {
      this.populationService.currentPopulation.next(
        this.currActivity.populations[this.questionNumber]
      );
      this.setSeenHints(state);
    }
    this.startPageProgressBar(state.progressNodes);
    this.elapsedTime = state.elapsedTime;
    this.timeElapsedService.setTime(this.elapsedTime);
    this.isAssignment = state.isAssignment;
    this.currentActivityOid = state.currentActivityOid;
    this.activitySeed = state.activitySeed ?? '';
    this.seedrandom = seedrandom(this.activitySeed);
  }

  /**
   * Create the progress bar, set up the header, then route according to the activity state.
   */
  startActivity(): void {
    this.createProgressBar();
    this.advanceActivity();
  }

  /**
   * Create progress bar based on the current activity state.
   */
  createProgressBar(): void {
    const progressBarNodes: IProgressNodeData[] = [];
    let qNum = 1;

    for (const piece of this.currActivity.activityManifest) {
      switch (piece.type) {
        case ActivityObjectTypes.VIDEO: {
          progressBarNodes.push({
            index: progressBarNodes.length,
            type: ProgressNodeTypeEnum.videoNodeComp,
            data: {},
            focus: ProgressNodeFocusEnum.inactive,
            status: ProgressNodeStatusEnum.none,
          });
          break;
        }
        case ActivityObjectTypes.QUESTION: {
          progressBarNodes.push({
            index: progressBarNodes.length,
            type: ProgressNodeTypeEnum.progressNodeComp,
            data: { text: `${qNum}` },
            focus: ProgressNodeFocusEnum.inactive,
            status: ProgressNodeStatusEnum.none,
          });
          qNum++;
          break;
        }
      }
    }
    console.log("we're creating progress bar with", progressBarNodes);
    this.startPageProgressBar(progressBarNodes);
  }

  /**
   * Helper function to set header show flags if they've changed.
   * @param headerConfig - The header properties to set
   * @param pageRoute - The page route to navigate to
   */
  setupHeader(headerConfig: HeaderConfig, pageRoute: string): void {
    this.showHeader.next(headerConfig.showHeader ?? true);
    this.showHeaderTitle.next(headerConfig.showTitle ?? true);
    this.showProgressBar.next(headerConfig.showProgressBar ?? true);
    this.showRewards.next(headerConfig.showRewards ?? true);
    this.showElapsedTime.next(headerConfig.showElapsedTime ?? true);
    this.showSecondaryHeader.next(headerConfig.showSecondaryHeader ?? true);
    this.pageRoute.next(pageRoute);
  }

  /**
   * Based on activity state set up the header flags, and navigate to the appropriate url.
   */
  // eslint-disable-next-line complexity
  setHeaderAndRoute(): void {
    if (!this.showInternal.getValue()) {
      this.showInternal.next(true);
    }
    if (!this.shouldRoute) return;

    let route = '/';
    console.log(`we're now setHeaderAndRoute`, this.activityState);
    const isPreK2User = this.isPreK2ReadingActivity();

    switch (this.activityState) {
      case ActivityStateEnum.START:
        if (isPreK2User) {
          this.setupHeader(
            {
              showTitle: false,
              showProgressBar: false,
              showRewards: false,
              showElapsedTime: false,
              showSecondaryHeader: false,
            },
            '/start-PK2'
          );
          route = 'start-PK2';
        } else {
          this.setupHeader(
            {
              showProgressBar: false,
              showElapsedTime: false,
              showSecondaryHeader: false,
            },
            '/start'
          );
          route = 'start';
        }
        break;
      case ActivityStateEnum.SELF_RATE:
        this.setupHeader(
          {
            showRewards: false,
            showSecondaryHeader: false,
          },
          '/self-rating'
        );
        route = 'self-rating';
        break;
      case ActivityStateEnum.VIDEO:
        this.setupHeader(
          {
            showProgressBar: !isPreK2User,
            showRewards: false,
            showSecondaryHeader: !isPreK2User,
          },
          '/video'
        );
        route = 'video';
        break;
      case ActivityStateEnum.QUESTION:
        this.setupHeader(
          {
            showRewards: false,
            showTitle: false,
          },
          '/tei'
        );
        route = 'tei';
        break;
      case ActivityStateEnum.RESULTS:
        if (isPreK2User) {
          this.setupHeader(
            {
              showProgressBar: false,
              showRewards: true,
              showElapsedTime: false,
              showSecondaryHeader: false,
            },
            '/results-PK2'
          );
          route = 'results-PK2';
        } else {
          this.setupHeader(
            {
              showProgressBar: false,
              showRewards: false,
              showElapsedTime: false,
              showSecondaryHeader: false,
            },
            '/results'
          );
          route = 'results';
        }
        break;
      case ActivityStateEnum.EXIT_SURVEY:
        this.setupHeader(
          {
            showProgressBar: false,
            showRewards: false,
            showElapsedTime: false,
            showSecondaryHeader: false,
          },
          '/survey'
        );
        route = 'survey';
        break;
      case ActivityStateEnum.SUCCESS_CRITERIA:
        this.setupHeader(
          {
            showProgressBar: false,
            showRewards: false,
            showElapsedTime: false,
            showSecondaryHeader: false,
          },
          '/success-criteria-PK2'
        );
        route = 'success-criteria-PK2';
        break;
      case ActivityStateEnum.PRE_START_SCREEN:
        this.setupHeader(
          {
            showTitle: false,
            showProgressBar: false,
            showRewards: false,
            showElapsedTime: false,
            showSecondaryHeader: false,
          },
          '/pre-start'
        );
        route = 'pre-start';
        break;
    }
    // allow to play SFX and then proceed to the next page
    setTimeout(() => {
      this.router
        .navigate([route], {
          skipLocationChange: true,
        })
        .then(() => {
          this.resetFocus();
        })
        .catch(err => console.log(err));
    }, 0);
  }

  resetFocus(): void {
    document.getElementById('top-focus-anchor')?.focus();
  }

  goToProgressNodeStep(
    node: IProgressNodeData,
    curHintsRevealed?: ISlideData[],
    selectedAnswers?: (string | number)[][]
  ): void {
    this.clickedOnProgNodeFlag = true;
    const startProgStep = this.findFirstProgNodeIndex();

    if (startProgStep !== -1) {
      this.prevStep = this.currStep;
      this.currStep = node.index + startProgStep;
      const activityStep = this.currActivity.activityManifest[this.currStep];
      const popIndex = this.resetPopulationService(activityStep);
      this.questionNumber = popIndex;

      const activityState = this.exportIncompleteActivityState();
      if (curHintsRevealed) {
        activityState.curHintsRevealed = curHintsRevealed;
      }

      // handle activity state pop resume info and resuming videos
      this.handleResumingPopAndVideo(popIndex, activityStep, activityState);

      // mutates activity state to keep or remove selected answers based on teacher status
      const selAnswer = selectedAnswers ?? activityState.selectedAnswers;
      if (selAnswer && popIndex !== -1) {
        this.handleResumingNonAssessableTypes(
          popIndex,
          activityState,
          selAnswer
        );
      }

      this.pageProgressService.newFocusIndex(node.index);
      this.currStep -= 1;
      if (this.useAnimationService.useAnimation$.getValue()) {
        this.popAnimationInStarted.pipe(take(1)).subscribe(() => {
          const activityStep =
            this.currActivity.activityManifest[this.currStep];
          this.handleResumeAftermath(activityState, activityStep, node);
        });
      }

      this.populationService.visualOverride.next(false);

      this.advanceActivity(true);

      if (!this.useAnimationService.useAnimation$.getValue()) {
        setTimeout(() => {
          this.handleResumeAftermath(activityState, activityStep, node);
        }, 0);
      }
    }

    this.clickedOnProgNodeFlag = false;
  }

  handleResumingPopAndVideo(
    popIndex: number,
    activityStep: ActivityManifestObject,
    activityState: IncompleteActivityState
  ): void {
    this.populationService.visualOverride.next(true);
    if (
      popIndex !== -1 &&
      !NonAssessableTypes.includes(
        this.populationService.populations[popIndex].templateID
      )
    ) {
      this.evaluatePopResponses(activityState, popIndex);
      this.resetPopulationService(activityStep);
    } else if (activityStep.type === ActivityObjectTypes.VIDEO) {
      this.setVideoSaveState(this.currStep);
    }
  }

  handleResumingNonAssessableTypes(
    popIndex: number,
    activityState: IncompleteActivityState,
    selectedAnswers: (string | number)[][]
  ): void {
    const isNonAssessableType = NonAssessableTypes.includes(
      this.populationService.populations[popIndex].templateID
    );

    if (isNonAssessableType) {
      if (
        !this.authSessionService.isTeacher ||
        (this.authSessionService.isTeacher &&
          !this.featureFlagService.isFlagEnabled(FeatureFlags.TEACHER_MODE))
      ) {
        activityState.selectedAnswers = selectedAnswers;
      } else {
        activityState.selectedAnswers = [[]];
      }
    }
  }

  resetPopulationService(activityStep: ActivityManifestObject): number {
    let popIndex = -1;
    if (activityStep.type === ActivityObjectTypes.QUESTION) {
      popIndex = this.currActivity.activityManifest
        .filter(a => a.type === ActivityObjectTypes.QUESTION)
        .findIndex(
          q => q.id === this.currActivity.activityManifest[this.currStep].id
        );

      this.populationService.filledAnswers.next([[]]);
      this.populationService.selectedAnswers.next([[]]);
      this.populationService.disabledAnswers.next([[]]);
      this.populationService.lastFilledCheck = 0;
      this.populationService.currTry = 0;
    }

    return popIndex;
  }

  evaluatePopResponses(
    activityState: IncompleteActivityState,
    popIndex: number
  ): IncompleteActivityState {
    activityState.currTry =
      activityState.populationResponses[popIndex].responses.length;
    activityState.questionNumber = popIndex;
    const questionNumber = this.questionNumber;
    this.questionNumber = popIndex;
    this.populationService.currentPopulation.next(
      this.populationService.populations[this.questionNumber]
    );
    const answers = this.populationService.getCurrentPopulationData().answers;
    const tempType =
      this.populationService.currentPopulation.getValue()?.templateID;

    activityState.populationResponses[popIndex].responses.forEach(
      (response, index) => {
        this.populationService.setSelectedAnswers(response);
        this.populationService.setDisabledAnswers(
          this.populationService.getCurrentPopulationData()
        );
        const finishedPop =
          activityState.answerStates[popIndex] !== AnswerStateEnum.INCOMPLETE &&
          activityState.populationResponses[popIndex].responses.length - 1 ===
            index;

        this.populationService.lastFilledCheck = index - 1;

        this.populationService.getCorrectAnswers(
          index,
          tempType ?? TemplateID.NONE,
          this.populationService.selectedAnswers,
          answers,
          finishedPop,
          true
        );
      }
    );

    activityState.filledAnswers =
      this.populationService.filledAnswers.getValue();
    activityState.selectedAnswers =
      this.populationService.selectedAnswers.getValue();
    activityState.disabledAnswers =
      this.populationService.disabledAnswers.getValue();
    this.questionNumber = questionNumber;

    return activityState;
  }

  setVideoSaveState(step: number): void {
    const index = this.currActivity.activityManifest
      .filter(a => a.type === ActivityObjectTypes.VIDEO)
      .findIndex(v => v.id === this.currActivity.activityManifest[step].id);
    if (index !== -1) {
      this.videoSaveStateService.getVideoSaveStateWithId(
        this.currActivity.videos[index].id
      );
    }
  }

  handleResumeAftermath(
    activityState: IncompleteActivityState,
    activityStep: ActivityManifestObject,
    node: IProgressNodeData
  ): void {
    if (activityStep.type === ActivityObjectTypes.QUESTION) {
      this.handleResumePopulation(activityState);
    }
    const isPreK2ReadingActivity = this.isPreK2ReadingActivity();
    if (node.status !== ProgressNodeStatusEnum.none) {
      this.footerService.footerData = isPreK2ReadingActivity
        ? footerData.PREK2_NEXT_WITH_TOOLS
        : footerData.NEXT_WITH_TOOLS;
      this.footerService.enableFooterButton();
    } else {
      if (activityStep.type === ActivityObjectTypes.VIDEO) {
        this.footerService.footerData = isPreK2ReadingActivity
          ? footerData.PREK2_NO_TEXT_WITHOUT_TOOLS
          : footerData.NEXT_WITHOUT_TOOLS;
        this.footerService.enableFooterButton();
      } else {
        this.footerService.footerData = isPreK2ReadingActivity
          ? footerData.PREK2_OK_WITH_TOOLS
          : footerData.OK_WITH_TOOLS;
        this.footerService.disableFooterButton();
      }
    }
  }

  findStartProgressBarStep(): number {
    for (let i = 0; i < this.currActivity.activityManifest.length; i++) {
      const actType = this.currActivity.activityManifest[i].type;
      // For PreK2, startProgressBarStep is the success criteria step.
      if (
        this.isPreK2ReadingActivity() &&
        this.currActivity.activityManifest[this.currStep].type ===
          ActivityObjectTypes.QUESTION
      ) {
        if (actType === ActivityObjectTypes.SUCCESS_CRITERIA) {
          return i;
        } else {
          continue;
        }
      }
      if (
        actType === ActivityObjectTypes.QUESTION ||
        actType === ActivityObjectTypes.VIDEO
      ) {
        return i;
      }
    }
    return -1;
  }

  findLastProgressBarStep(): number {
    const startProgNodeStep = this.findStartProgressBarStep();
    if (startProgNodeStep === -1) {
      return -1;
    }

    return (
      startProgNodeStep +
      this.pageProgressService.AvailableProgressNodes.length -
      1
    );
  }

  isActivityFinished(): boolean {
    const allQuestionsAnswered =
      this.populationService.answerStates.findIndex(
        a => a === AnswerStateEnum.INCOMPLETE
      ) === -1;
    const allVideosSeen =
      this.pageProgressService.AvailableProgressNodes.filter(
        a => a.type === ProgressNodeTypeEnum.videoNodeComp
      ).findIndex(v => v.focus === ProgressNodeFocusEnum.inactive) === -1;
    return allQuestionsAnswered && allVideosSeen;
  }

  isCurrStepOnProgressBar(): boolean {
    if (
      this.currStep < 0 ||
      this.currActivity.activityManifest.length <= this.currStep
    ) {
      return false;
    }
    const objType = this.currActivity.activityManifest[this.currStep].type;
    return (
      objType === ActivityObjectTypes.QUESTION ||
      objType === ActivityObjectTypes.VIDEO
    );
  }

  /**
   * Move the activity forward one step
   * If we're in the question state, attempt to move on to the next question
   * if we've exhausted the populations, moves the state forward.
   * will ignore if attempting to advance beyond results state.
   */
  advanceActivity(disableSave?: boolean): void {
    this.prevStep = this.currStep;
    if (
      this.isCurrStepOnProgressBar() &&
      this.pageProgressService.isFocusedLastProgNode() &&
      this.currStep === this.findLastProgressBarStep()
    ) {
      this.fillIncompleteAnswersIncorrect();
    }
    if (++this.currStep >= this.currActivity.activityManifest.length) {
      console.error(
        "We're at the end of the activity, probably should have been handled better.",
        this.currStep,
        this.currActivity.activityManifest.length
      );
      if (!this.referrerService.isEducatorReferrer()) {
        this.resetActivityAndNavigateHome();
      }
      return;
    }
    console.log(
      'ActivityService - advancing to step',
      this.currStep,
      this.currActivity.activityManifest[this.currStep],
      'from state',
      ActivityStateEnum[this.activityState]
    );

    this.loggingService.logInfo('activity_advance', {
      timeElapsed: this.timeElapsedService.getTimeAsSeconds(),
      step: this.currStep,
      fromState: ActivityStateEnum[this.activityState].toString(),
      toState: this.currActivity.activityManifest[this.currStep].toString(),
      activityType: this.currActivity.activityManifest[this.currStep].type,
      displayStatus: this.activityDisplayStatus,
    });

    this.handleNextActivityObjectType();
    // handleNextActivityObjectType may recursively call advanceActivity when skipping steps
    // (e.g. SKIP_SPOT_SCENES), which can push currStep past the end of the manifest.
    if (this.currStep >= this.currActivity.activityManifest.length) {
      return;
    }
    this.reportCurrentInterfaceToAnalytics();
    if (
      !disableSave &&
      this.featureFlagService.isFlagEnabled(FeatureFlags.AUTOSAVE)
    ) {
      this.saveActivityOnNewStepType();
    }
    this.inactivityService.pauseInactivityTracking();
    this.inactivityService.resumeInactivityTracking();
    this.advanceActivityEvent.emit();
  }

  // eslint-disable-next-line complexity
  private handleNextActivityObjectType(): void {
    switch (this.currActivity.activityManifest[this.currStep].type) {
      case ActivityObjectTypes.START_SCREEN: {
        if (
          this.featureFlagService.isFlagEnabled(
            FeatureFlags.SKIP_SPOT_SCENES
          ) &&
          this.isPreK2ReadingActivity()
        ) {
          this.advanceActivity(true);
          return;
        }
        this.activityState = ActivityStateEnum.START;
        this.setHeaderAndRoute();
        break;
      }
      case ActivityObjectTypes.ENTRY_SURVEY: {
        this.activityState = ActivityStateEnum.SELF_RATE;
        this.setHeaderAndRoute();
        break;
      }
      case ActivityObjectTypes.VIDEO: {
        this.processAdvanceVideo();
        // If it is a PreK2 reading activity, loading audio for the first TEI will be handled in SC screen.
        if (!this.isPreK2ReadingActivity()) {
          this.preCheckToLoadAudioData();
        }
        break;
      }
      case ActivityObjectTypes.QUESTION: {
        if (this.activityDisplayStatus !== InboxDisplayStatus.IN_PROGRESS) {
          this.updateDisplayStatus(InboxDisplayStatus.IN_PROGRESS); // update display status to in progress if not already set after reaching the first TEI
        }
        const population = this.getAssetData(
          this.currActivity.activityManifest[this.currStep]
        ) as Population;
        if (population?.verbal) {
          this.processAdvanceQuestionWithAudio(population);
        } else {
          this.processAdvanceQuestion();
        }
        break;
      }
      case ActivityObjectTypes.EXIT_SURVEY: {
        this.activityState = ActivityStateEnum.EXIT_SURVEY;
        this.setHeaderAndRoute();
        break;
      }
      case ActivityObjectTypes.RESULTS_SCREEN: {
        if (
          this.featureFlagService.isFlagEnabled(FeatureFlags.SKIP_SPOT_SCENES)
        ) {
          this.advanceActivity(true);
          return;
        }
        this.activityState = ActivityStateEnum.RESULTS;
        this.setHeaderAndRoute();
        break;
      }
      case ActivityObjectTypes.SUCCESS_CRITERIA: {
        if (
          this.featureFlagService.isFlagEnabled(FeatureFlags.SKIP_SPOT_SCENES)
        ) {
          this.advanceActivity(true);
          return;
        }
        this.activityState = ActivityStateEnum.SUCCESS_CRITERIA;
        this.setHeaderAndRoute();
        // Load audio data for the first TEI in PreK2 reading activities
        this.preCheckToLoadAudioData();
        break;
      }
      case ActivityObjectTypes.PRE_START_SCREEN: {
        this.activityState = ActivityStateEnum.PRE_START_SCREEN;
        this.setHeaderAndRoute();
        break;
      }
    }
  }

  /**
   * Load Audio data for the next verbal population if the call is not already in progress
   * or if the audio data is not already loaded.
   */
  private loadAudioDataForNextPopulation(population: Population): void {
    this.verbalService.populationToLoad = population;
    if (!this.verbalService.populationsAudioDataMap[population.id]) {
      this.verbalService.loadCurrentPopulation();
    }
  }

  /**
   * Checks if the next question is verbal and loads audio data if it is.
   * This is called before advancing to the next question to ensure audio data is ready.
   */
  private preCheckToLoadAudioData(): void {
    const nextManifestObject =
      this.currActivity.activityManifest[this.currStep + 1];
    if (nextManifestObject?.type === ActivityObjectTypes.QUESTION) {
      const nextQuesPop = this.getAssetData(nextManifestObject) as Population;
      if (nextQuesPop?.verbal) {
        // if next question is verbal, then we need to load the audio data
        this.loadAudioDataForNextPopulation(nextQuesPop);
      }
    }
  }

  private async processAdvanceQuestionWithAudio(
    population: Population
  ): Promise<void> {
    const popAudioData =
      this.verbalService.populationsAudioDataMap[population.id];
    if (!popAudioData || popAudioData.status === AudioDataLoadStatus.LOADING) {
      // If popAudioData is not present, make the call to load all the audio data
      if (!popAudioData) {
        this.loadAudioDataForNextPopulation(population);
      }
      // Show loading screen while waiting for audio data to load
      this.loadingScreenService.loadingScreenState.set(
        LoadingScreenState.shown
      );
      // Wait for the audio data to load or timeout after 10 seconds
      await this.waitForIntervals(500, 20, (): boolean => {
        const audioStatus =
          this.verbalService.populationsAudioDataMap[population.id]?.status;
        return (
          audioStatus === AudioDataLoadStatus.LOADED ||
          audioStatus === AudioDataLoadStatus.ERROR
        );
      });

      // If the audio data is loaded, hide the loading screen and proceed
      // If the audio data is not loaded, show an error message
      this.loadingScreenService.loadingScreenState.set(
        LoadingScreenState.hidden
      );
      if (
        this.verbalService.populationsAudioDataMap[population.id]?.status ===
        AudioDataLoadStatus.LOADED
      ) {
        this.verbalService.currentPopulationId = population.id;
        this.processAdvanceQuestion();
      } else {
        this.messageService.showMessage(MessageCodes.HTTP_UNEXPECTED_NET_ERROR);
      }
      return;
    } else if (popAudioData.status === AudioDataLoadStatus.LOADED) {
      this.verbalService.currentPopulationId = population.id;
    } else if (popAudioData.status === AudioDataLoadStatus.ERROR) {
      this.loadingScreenService.loadingScreenState.set(
        LoadingScreenState.hidden
      );
      this.messageService.showMessage(MessageCodes.HTTP_UNEXPECTED_NET_ERROR);
      return;
    }
    this.processAdvanceQuestion();
  }

  /**
   * Executes a callback function at specified intervals until it returns true or reaches the maximum number of executions.
   * @param intervalMs - The time interval in milliseconds between each execution of the callback
   * @param count - The maximum number of times to execute the callback
   * @param callback - A function that returns a boolean. When it returns true, the interval stops
   * @returns A Promise that resolves to true when either the callback returns true or the maximum count is reached
   */
  private waitForIntervals(
    intervalMs: number,
    count: number,
    callback: () => boolean
  ): Promise<boolean> {
    return new Promise(resolve => {
      let executions = 0;
      const interval = setInterval(() => {
        if (callback?.() || executions >= count) {
          clearInterval(interval);
          resolve(true); // Callback returned true, resolve the promise
          return;
        }
        executions++;
      }, intervalMs);
    });
  }

  private processAdvanceVideo(): void {
    this.setQuestionNumberFromStep();
    const startProgBarStep = this.findStartProgressBarStep();
    if (startProgBarStep !== -1) {
      this.updateProgressBar(this.currStep - this.findStartProgressBarStep());
    }
    if (this.activityState !== ActivityStateEnum.VIDEO) {
      this.activityState = ActivityStateEnum.VIDEO;
      this.setHeaderAndRoute();
    }

    this.processVideoState(this.currStep).catch(() =>
      console.log('Error processing video save state data')
    );
  }

  async processVideoState(step: number): Promise<void> {
    const video = this.getAssetData(this.currActivity.activityManifest[step]);

    if (video && video.id) {
      this.videoData$.next(video as VideoData);
      return this.videoSaveStateService.getVideoSaveStateWithId(video.id);
    }

    return Promise.reject();
  }

  private processAdvanceQuestion(fromVerbalCallback = false): void {
    this.preCheckToLoadAudioData(); // check and load audio data for the next population
    this.setQuestionNumberFromStep();
    const startProgBarStep = this.findStartProgressBarStep();
    if (startProgBarStep !== -1) {
      this.updateProgressBar(this.currStep - this.findStartProgressBarStep());
    }

    this.timeElapsedService.startTimer();
    const pop = this.getAssetData(
      this.currActivity.activityManifest[this.currStep]
    );
    if (pop) {
      // skip resume question flow when returning from verbal callback to avoid circular logic
      if (this.tryResumeQuestion(fromVerbalCallback)) return;

      this.populationService.nextQuestion(
        pop as Population,
        this.populationService.populations.length - 1 <= this.questionNumber,
        this.isResumingQuestion(this.questionNumber) // to avoid adding the question event to clickstream again
      );
      if (SaveIntervalTypes.includes((pop as Population).templateID)) {
        this.startSaveInterval(this.saveIntervalTime);
      } else {
        this.stopSaveInterval();
      }
    }
    if (this.activityState !== ActivityStateEnum.QUESTION) {
      this.activityState = ActivityStateEnum.QUESTION;
      this.setHeaderAndRoute();
    }

    if (this.populationService.currentPopulation.value?.verbal) {
      this.verbalService
        .playPopulationAudio(VerbalAudioType.PROMPT, true)
        .catch(err => {
          console.error('Error playing population audio:', err);
          this.populationService.setDisableToolbar(false);
        });
      this.populationService.setDisableToolbar(true); // disable toolbar buttons when the call to play prompt audio is made
    }

    const curPop = this.populationService.currentPopulation.value;
    if (curPop) {
      this.populationService.parseVideoData(curPop);
    }
  }

  private tryResumeQuestion(fromVerbalCallback: boolean): boolean {
    if (
      this.isResumingQuestion(this.questionNumber) &&
      !this.clickedOnProgNodeFlag &&
      !fromVerbalCallback
    ) {
      const node = this.pageProgressService.getQuestionNode(
        this.questionNumber
      );
      if (node) {
        this.goToProgressNodeStep(node);
        return true;
      }
    }
    return false;
  }

  setQuestionNumberFromStep(): void {
    let questionNumber = -1;
    for (let i = 0; i < this.currActivity.activityManifest.length; i++) {
      if (this.currStep < i) {
        break;
      }
      const { type } = this.currActivity.activityManifest[i];
      if (type === ActivityObjectTypes.QUESTION) {
        questionNumber++;
      }
    }
    if (questionNumber !== -1) {
      this.questionNumber = questionNumber;
    }
  }

  isResumingQuestion(questionNumber: number): boolean {
    if (
      questionNumber < 0 ||
      questionNumber >= this.populationService.answerStates.length ||
      questionNumber >= this.populationService.populationResponses.length
    ) {
      return false;
    }
    const answerStateNotIncomplete =
      this.populationService.answerStates[this.questionNumber] !==
      AnswerStateEnum.INCOMPLETE;
    if (answerStateNotIncomplete) {
      return true;
    } else {
      const responses =
        this.populationService.populationResponses[questionNumber].responses;
      const havePopulationResponses =
        responses.length > 0 &&
        responses[0].length > 0 &&
        responses[0][0].length > 0 &&
        responses[0][0][0].toString().length > 0;
      return havePopulationResponses;
    }
  }

  async saveActivityOnNewStepType(): Promise<void> {
    const curType = this.currActivity.activityManifest[this.currStep]
      .type as ActivityObjectTypes;
    // only attempt to save if on a different type of page than last step
    if (
      this.currStep === 0 ||
      this.currActivity.activityManifest[this.currStep - 1].type !== curType
    ) {
      // only submit save if type within save types or if has 'saveActivityAnywhere' flag
      if (this.shouldSaveActivityState(curType)) {
        await this.submitActivityData(true);
      } else {
        // remove activity from resumes cause most likely either done or started over
        const resumeActivityIds =
          await this.activityStateService.getSavedActivityIds();

        if (
          resumeActivityIds.findIndex(id => id === this.currActivity.id) !== -1
        ) {
          await this.activityStateService.removeIncompleteActivity(
            this.currActivity.id
          );
        }
      }
    }
  }

  shouldSaveActivityState(curType: ActivityObjectTypes): boolean {
    return (
      ActivityService.SAVE_ON_STATE_TYPES.findIndex(
        saveType => saveType === curType
      ) !== -1 ||
      this.featureFlagService.isFlagEnabled(FeatureFlags.SAVE_ACTIVITY_ANYWHERE)
    );
  }

  hasAnswerChanged(): boolean {
    return (
      this.populationService.selectedAnswers.getValue() !==
      this.populationService.prevSelectedAnswers
    );
  }

  savedChangedAnswers(
    savedAnswers: (string | number)[][],
    prevSelectedAnswers: (string | number)[][]
  ): void {
    // check if the past prev selected is the current prev selected
    // if not then want the save interval to save on next timeout
    if (prevSelectedAnswers === this.populationService.prevSelectedAnswers) {
      this.populationService.prevSelectedAnswers = savedAnswers;
    }
  }

  startSaveInterval(time: number): void {
    this.stopSaveInterval();
    this.saveInterval = setInterval(async () => {
      const curType = this.currActivity.activityManifest[this.currStep]
        .type as ActivityObjectTypes;
      const isQuestion = curType === ActivityObjectTypes.QUESTION;
      const shouldSave = isQuestion && this.hasAnswerChanged();
      const prevSelectedAnswers = this.populationService.prevSelectedAnswers;
      const selectedAnswers = this.populationService.selectedAnswers.getValue();
      if (this.shouldSaveActivityState(curType) && shouldSave) {
        await this.submitActivityData(true);
        if (isQuestion) {
          this.savedChangedAnswers(selectedAnswers, prevSelectedAnswers);
        }
        // for the constructed response TEIs we need to save the answers as submitted
        this.populationService.submittedSelectedAnswers = selectedAnswers;
      }
    }, time);
  }

  stopSaveInterval(): void {
    if (this.saveInterval) {
      clearInterval(this.saveInterval);
    }
  }

  async submitActivityData(resumeableActivity?: boolean): Promise<void> {
    if (
      this.authSessionService.isTeacher &&
      !this.featureFlagService.isFlagEnabled(
        FeatureFlags.SAVE_ACTIVITY_ANYWHERE
      )
    ) {
      console.log('Teacher mode is enabled. Progress is not saved.');
      return;
    }
    console.log(
      'submitActivityData',
      resumeableActivity,
      this.exportIncompleteActivityState()
    );

    if (!resumeableActivity) {
      this.resultsService.earnedStars =
        this.resultsService.getCurrentNumEarnedStars();
      this.trackingService.push({
        event: 'Activity Complete',
        timeElapsed: this.timeElapsedService.getTimeAsSeconds(),
      });
      this.trackingService.push({
        event: 'Video Time Watched',
        timeElapsed:
          this.videoSaveStateService.curVideoState$.getValue()
            .videoTotalWatchtime,
      });
      this.trackingService.push({
        timeElapsed: undefined,
      });
      await this.submitActivityCompleted();
    } else {
      const pop = this.getAssetData(
        this.currActivity.activityManifest[this.currStep]
      );
      if (
        pop &&
        SaveAnswerStatesTypes.includes((pop as Population).templateID)
      ) {
        this.populationService.submittedSelectedAnswers =
          this.populationService.selectedAnswers.value;
      }
      await this.saveActivity();
    }
  }

  async submitActivityCompleted(): Promise<void> {
    const completeActivityState: CompleteActivityState = {
      activityId: this.currActivity.id,
      dateCompleted: Date.now().toString(),
      timeToCompleteSecs: this.timeElapsedService.getTimeAsSeconds(),
      starsEarned: this.resultsService.earnedStars,
      score: this.resultsService.getPercentage(),
      answerStates: this.populationService.answerStates,
      inboxActivityId: this.inboxActivityId.getValue() ?? '',
      inboxAssignmentId: this.inboxAssignmentId ?? '',
    };

    this.loggingService.logInfo('activity_completed', {
      completeActivityState: completeActivityState,
      displayStatus: this.activityDisplayStatus,
    });

    try {
      this.activitySubmitInProgress$.next(true);

      // update display status on student inbox SRS
      await this.updateDisplayStatus(InboxDisplayStatus.COMPLETED);

      // save to KB as a 'completed' activity
      if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB)) {
        // construct the clickstream log
        if (
          this.featureFlagService.isFlagEnabled(FeatureFlags.USE_CLICKSTREAM)
        ) {
          this.constructClickstreamService.generateClickstream(
            this.exportIncompleteActivityState(),
            this.currActivity,
            this.assignmentService.contentOid.getValue()
          );
        }
        // update user stars
        await this.setKBServiceStars();
        await this.activityStateService.addCompleteActivityStateAndSync(
          completeActivityState,
          false
        );
        // will be the final step that syncs kb cache and sends api request
        await this.activityStateService.removeIncompleteActivity(
          this.currActivity.id
        );
      } else if (
        !this.featureFlagService.isFlagEnabled(
          FeatureFlags.SAVE_ACTIVITY_ANYWHERE
        ) // dev feature
      ) {
        await this.activityStateService.removeIncompleteActivity(
          this.currActivity.id
        );
      }
    } finally {
      this.activitySubmitInProgress$.next(false);
    }
  }

  async updateDisplayStatus(
    status = InboxDisplayStatus.COMPLETED
  ): Promise<void> {
    if (
      !this.featureFlagService.isFlagEnabled(
        FeatureFlags.ENABLE_STUDENT_INBOX
      ) ||
      this.authSessionService.isTeacher // do not update display status if teacher mode is enabled
    ) {
      console.warn('Student inbox is disabled. Cannot update display status.');
      return;
    }

    this.activityDisplayStatus = status;

    const input: InboxUpdateInput = {
      activityId: this.inboxActivityId.getValue() ?? '',
      displayStatus: status,
    };

    await this.studentInboxService
      .updateActivityStatus(input)
      .then(result => {
        if (!result) {
          console.error('Failed to update activity status');
        }
      })
      .catch(err => console.error(err));
  }

  async setKBServiceStars(): Promise<void> {
    const currentStars = this.userService.stars$.getValue() ?? 0;

    if (
      this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB) &&
      (await this.kbService.get('MainMenu', 'Stars')) !==
        currentStars + this.resultsService.earnedStars
    ) {
      this.kbService.set(
        'MainMenu',
        'Stars',
        currentStars + this.resultsService.earnedStars
      );
    }
  }

  /**
   * handles progress bar state update when click on the next button
   */
  updateProgressBarState(): void {
    this.pageProgressService.updateProgressFromAnswerStates(
      this.populationService.answerStates
    );
  }

  nextProgressBarFocus(): void {
    this.pageProgressService.newFocusIndex();
  }

  findFirstProgNodeIndex(): number {
    for (let i = 0; i < this.currActivity.activityManifest.length; i++) {
      const step = this.currActivity.activityManifest[i];
      // For PreK2, startProgressBarStep is the success criteria step.
      if (
        this.isPreK2ReadingActivity() &&
        this.currActivity.activityManifest[this.currStep].type ===
          ActivityObjectTypes.QUESTION
      ) {
        if (step.type === ActivityObjectTypes.SUCCESS_CRITERIA) {
          return i;
        } else {
          continue;
        }
      }
      if (
        step.type === ActivityObjectTypes.VIDEO ||
        step.type === ActivityObjectTypes.QUESTION
      ) {
        return i;
      }
    }
    return -1;
  }

  fillIncompleteAnswersIncorrect(): void {
    for (let i = 0; i < this.populationService.answerStates.length; i++) {
      const a = this.populationService.answerStates[i];
      if (a === AnswerStateEnum.INCOMPLETE) {
        this.populationService.answerStates[i] = AnswerStateEnum.INCORRECT;
        this.resultsService.numIncorrect++;
      }
    }
  }

  getTargetResultData(): TargetResultData[] {
    const targetResultData: TargetResultData[] = [];
    const sub = this.learningTarget.subscribe(targets => {
      targets.forEach(target => {
        for (let i = 0; i < target.successCriteria.length; i++) {
          const sucCrit = target.successCriteria[i];
          const id = sucCrit.id;
          if (!id) {
            return;
          }
          targetResultData.push({
            id: id,
            imagePath: this.getRandomTargetImage(),
            totalCorrect: `${this.populationService.getTotalCorrectTargets(
              id
            )}`,
            totalAssessed: `${this.populationService.getTotalTargets(id)}`,
            description: sucCrit.text,
          });
        }
      });
    });
    sub.unsubscribe();

    return targetResultData;
  }

  getRandomTargetImage(): string {
    return '/assets/learning-target/diamond.svg';
  }

  //#region Progress Bar
  savePageProgressBar(): void {
    localStorage.setItem(
      ActivityService.PAGE_BAR_LOCAL_STORAGE,
      JSON.stringify(this.pageProgressService.AvailableProgressNodes)
    );
  }

  loadPageProgressBar(): void {
    let result: Array<IProgressNodeData> = [];
    const barData = localStorage.getItem(
      ActivityService.PAGE_BAR_LOCAL_STORAGE
    );
    if (barData) {
      result = JSON.parse(barData);
    }
    this.startPageProgressBar(result);
  }

  startPageProgressBar(
    pageProgressData: IProgressNodeData[],
    offset?: number
  ): void {
    this.pageProgressService.progressService.Config = this.pageProgressConfig;
    this.currentPage = this.findCurrentPage(pageProgressData) + (offset ?? 0);
    this.pageProgressService.AvailableProgressNodes = pageProgressData;
    this.pageProgressService.newFocusIndex(this.currentPage);
    if (offset) {
      this.pageProgressService.setPreviousToSeen();
    }
  }

  isCurrentPopulationAnswered(): boolean {
    if (
      this.pageProgressService.AvailableProgressNodes &&
      this.pageProgressService.AvailableProgressNodes.length > 0
    ) {
      const node = this.pageProgressService.AvailableProgressNodes.find(
        p => p.focus === ProgressNodeFocusEnum.active
      );
      return node?.status !== ProgressNodeStatusEnum.none;
    }
    return false;
  }

  isCurrentPopulationIncorrect(): boolean {
    if (
      this.pageProgressService.AvailableProgressNodes &&
      this.pageProgressService.AvailableProgressNodes.length > 0
    ) {
      const node = this.pageProgressService.AvailableProgressNodes.find(
        p => p.focus === ProgressNodeFocusEnum.active
      );
      return node?.status === ProgressNodeStatusEnum.wrong;
    }
    return false;
  }

  findCurrentPage(pageProgressData: IProgressNodeData[]): number {
    let pageNum = 0;

    for (let i = 0; i < pageProgressData.length; i++) {
      const page = pageProgressData[i];
      if (page.focus === ProgressNodeFocusEnum.active) {
        pageNum = i;
        break;
      }
    }

    return pageNum;
  }

  updateProgressBar(newIndex: number): void {
    this.currentPage = newIndex;
    const isComplete = this.completionCheck();
    if (!isComplete) {
      this.pageProgressService.newFocusIndex(newIndex);
    }
    this.savePageProgressBar();
  }

  completionCheck(): boolean {
    return (
      this.currentPage >= this.pageProgressService.AvailableProgressNodes.length
    );
  }

  //#endregion

  //#region Activity State

  resetActivityState(): void {
    this.resultsService.numPerfect = 0;
    this.resultsService.numCorrect = 0;
    this.resultsService.numIncorrect = 0;
    this.updateStarCounter();
    this.activityState = ActivityStateEnum.INVALID;
    this.populationService.populationResponses = new Array(
      this.populationService.populations.length
    )
      .fill({})
      .map(() => {
        return {
          responses: [],
          elapsedTime: 0,
        };
      });
    this.populationService.answerStates = new Array<AnswerStateEnum>(
      this.populationService.populations.length
    ).fill(AnswerStateEnum.INCOMPLETE);
    this.populationService.currTry = 0;
    this.populationService.selectedAnswers.next([]);
    this.populationService.disabledAnswers.next([]);
    this.populationService.prevSelectedAnswers = [];
    this.showHeader.next(false);
    this.showSecondaryHeader.next(false);
    this.verbalService.stopCurrent();
    this.populationService.setDisableToolbar(false);
    this.activitySubmitInProgress$.next(false);
    this.populationService.currentPopulation.next(undefined);
    this.toolsService.clearTools();
    this.videoService.resetWatchTime();
    this.seriesName.next('');
    this.episodeTitle.next('');
    this.episodeTitleVerbal.next('');
    this.skillName$.next('');
    this.learningTarget.next([]);
    this.isAssignment = false;
    this.currentActivityOid = -1;
    this.activitySeed = '';
  }

  /**
   * Hide the header and navigate to the home page
   */
  resetActivityAndNavigateHome(): void {
    if (
      !this.featureFlagService.isFlagEnabled(FeatureFlags.RETURN_TO_REFERRER)
    ) {
      if (this.isSandboxActivity$.getValue()) {
        this.router.navigate(['/sandbox']);
      } else {
        this.router.navigate(['/']);
      }

      this.resetActivityState();
    } else {
      this.referrerService.navigateToReferrer();
    }
  }

  updateStarCounter(): void {
    if (this.resultsService.earnedStars === 0) {
      return;
    }
    const currentStars = this.userService.stars$.getValue() || 0;
    this.userService.stars$.next(
      currentStars + this.resultsService.earnedStars
    );
    this.resultsService.earnedStars = 0;
  }

  async saveActivity(): Promise<void> {
    if (this.currActivity) {
      const exportActivity = this.exportIncompleteActivityState();
      this.loggingService.logInfo('activity_save', {
        activityState: exportActivity,
        displayStatus: this.activityDisplayStatus,
      });
      await this.activityStateService.saveIncompleteActivity(exportActivity);
    }
  }

  /**
   * Attempt to save the activity state in local storage, and resetActivity
   */
  async exitActivity(): Promise<void> {
    this.stopSaveInterval();
    if (
      this.currActivity &&
      this.currActivity.activityManifest.length > this.currStep
    ) {
      const curType = this.currActivity.activityManifest[this.currStep]
        .type as ActivityObjectTypes;
      if (this.shouldSaveActivityState(curType)) {
        await this.submitActivityData(true);
      }
    }

    this.loggingService.logInfo('activity_exited', {
      displayStatus: this.activityDisplayStatus,
    });

    this.toolsService.deactivateAllTools();
    this.inactivityService.endInactivityTracking();
    setTimeout(() => {
      //hack to run this during the next tick to fix ExpressionChangedAfterItHasBeenCheckedError
      this.footerService.show = false;
    });
    this.resetActivityAndNavigateHome();
  }

  /**
   * Gets the current step from the activity manifest, if it's a question gets the TEI interface key name.
   * @returns string representing the current location in the activity
   */
  getCurrentInterface(): string {
    const currStep = this.currActivity.activityManifest[this.currStep]
      .type as ActivityObjectTypes;
    if (currStep === ActivityObjectTypes.QUESTION) {
      return this.populationService.getCurrentPopulationName();
    }
    return currStep;
  }

  /**
   * If the current interface is different from the previously reported interface, this function reports the current interface
   */
  reportCurrentInterfaceToAnalytics(): void {
    const currInterface = this.getCurrentInterface();
    if (this.lastReportedInterface !== currInterface) {
      this.trackingService.push({ interface: this.getCurrentInterface() });
      this.lastReportedInterface = currInterface;
    }
  }

  /**
   * Push the current activityId onto the analytics datalayer
   */
  reportCurrentActivityToAnalytics(): void {
    this.trackingService.push({ activityId: this.currActivity.id });
  }

  exportIncompleteActivityState(): IncompleteActivityState {
    const activityState: IncompleteActivityState = {
      id: this.currActivity.id,
      currentStep: this.currStep - 1,
      questionNumber:
        this.questionNumber < 0 ? this.questionNumber : this.questionNumber - 1,
      numPerfect: this.resultsService.numPerfect,
      numCorrect: this.resultsService.numCorrect,
      numIncorrect: this.resultsService.numIncorrect,
      answerStates: this.populationService.answerStates,
      populationResponses: this.populationService.populationResponses,
      progressNodes: this.pageProgressService.AvailableProgressNodes,
      elapsedTime: this.timeElapsedService.getTimeAsSeconds(),
      currTry: this.populationService.currTry,
      filledAnswers: this.populationService.filledAnswers.getValue(),
      selectedAnswers: this.populationService.submittedSelectedAnswers,
      disabledAnswers: this.populationService.disabledAnswers.getValue(),
      curHintsRevealed: this.populationService
        .getCurrentPopulationHints()
        .filter(val => val.wasViewed ?? false),
      isAssignment: this.isAssignment,
      inboxActivityId: this.inboxActivityId.getValue() ?? '',
      inboxAssignmentId: this.inboxAssignmentId ?? '',
      currentActivityOid: this.currentActivityOid,
      lastOpened: Date.now().toString(),
      activitySeed: this.activitySeed,
      startTime: new Date(this.clickstreamService.getStartTime()).toString(),
      clickstreamInteractions:
        this.clickstreamService.getClickstreamInteractions(),
    };

    return activityState;
  }

  /**
   * Transforms provided period format activity id to slugiffied activityId
   * @param activityId An activity id in the previous format (Subject.Grade.etc...)
   * @returns An activity id with the new slug format (subject-grade-etc...)
   */
  private sluggifyActivityId(activityId: string): string {
    return activityId.split('.').join('-').toLowerCase();
  }

  /**
   * DEBUG USE ONLY
   * relaunches the current activity to be used with content preview only
   */
  relaunchContentPreview(): void {
    if (this.featureFlagService.isFlagEnabled(FeatureFlags.CONTENT_TESTING)) {
      this.resetActivityState();
      this.timeElapsedService.startTimer();
      this.initActivity();
    } else {
      console.warn(
        'Tried calling activity service relaunchContentPreview but contentTesting feature flag isnt true, please only use this for debug purposes only!'
      );
    }
  }

  // eslint-disable-next-line complexity
  filterActivityManifestBasedOnGrade(result: Activity): Activity {
    let newManifest = [];
    const isPreK2User = this.userService.grade$.getValue() < 3;
    if (
      isPreK2User &&
      result.activityManifest &&
      result.product === 'Reading' &&
      result.targetText
    ) {
      for (const activity of result.activityManifest) {
        if (
          activity.type === ActivityObjectTypes.ENTRY_SURVEY ||
          activity.type === ActivityObjectTypes.EXIT_SURVEY
        ) {
          continue;
        }
        if (activity.type === ActivityObjectTypes.START_SCREEN) {
          newManifest.push({
            type: ActivityObjectTypes.PRE_START_SCREEN,
          });
        }
        newManifest.push(activity);
        if (activity.type === ActivityObjectTypes.VIDEO) {
          newManifest.push({ type: ActivityObjectTypes.SUCCESS_CRITERIA });
        }
      }
    } else {
      newManifest = result.activityManifest;
    }

    return {
      ...result,
      activityManifest: newManifest,
    };
  }

  /**
   * Processes an Activity object by parsing special tokens within its populations.
   * This method handles various text fields across different population types and templates,
   * converting tokens into displayable text using the verbal service.
   *
   * The method processes the following elements:
   * - Hints
   * - First and second incorrect feedback
   * - Population question text
   * - Population text data
   * - Passages
   * - Multiple choice answer choices and their feedback
   * - Sorting bucket labels
   *
   * @param result - The Activity object containing populations to be processed
   * @returns A new Activity object with parsed display text for all relevant fields
   */
  parseTokensInPopulations(result: Activity): Activity {
    result.populations.forEach((pop: Population) => {
      const parsedPop = { ...pop };
      const {
        hint,
        feedbackFirstIncorrect,
        feedbackSecondIncorrect,
        populationData,
        passage,
        templateID,
      } = parsedPop;

      this.parseHints(hint);

      // parse first and second incorrect feedback
      this.parseFeedback(feedbackFirstIncorrect);
      this.parseFeedback(feedbackSecondIncorrect);

      // parse population question text
      this.parseQuestion(populationData?.question, parsedPop.verbal);

      // parse population text data as this will be used in few TEIs like inline choice / HotText
      this.parseTextData(populationData as PopulationData);

      // parse passage text
      this.parsePassage(passage);

      // parse ChoiceXXText and ChoiceXXFeedback for all MultipleChoice types
      if (MultipleChoiceTypes.includes(templateID)) {
        this.parseAnswerChoices(populationData as PopulationMultipleChoice);
      }

      // parse Sorting bucket labels
      if (SortingBucketTypes.includes(templateID)) {
        this.parseSortingBuckets(populationData as PopulationDragAndDrop);
      }
    });

    return {
      ...result,
    };
  }

  parseHints(hints: ISlideData[] | undefined): void {
    // parse hint titles
    hints?.forEach(hintItem => {
      if (hintItem.data.title) {
        hintItem.data.displayText =
          this.verbalService.parseWordpartAndPhonemeTokens(
            hintItem.data.title
          ).displayStr;
      }
    });
  }

  parseFeedback(feedback: Feedback | undefined): void {
    if (feedback?.content) {
      feedback.displayText = this.verbalService.parseWordpartAndPhonemeTokens(
        feedback?.content ?? ''
      ).displayStr;
    }
  }

  parseQuestion(question: TextData | undefined, verbal: boolean = false): void {
    if (question?.text || question?.displayText) {
      const displayText: string = verbal
        ? question.displayText ?? ''
        : question.text;
      question.displayText =
        this.verbalService.parseWordpartAndPhonemeTokens(
          displayText
        ).displayStr;
    }
  }

  parsePassage(passage: TextData | undefined): void {
    if (passage?.text) {
      passage.displayText = this.verbalService.parseWordpartAndPhonemeTokens(
        passage?.text
      ).displayStr;
    }
  }

  parseTextData(popData: PopulationData | undefined): void {
    if (popData && 'text' in popData) {
      let text = popData.text;
      if (Array.isArray(text)) {
        text = text[0];
      }
      text.displayText = this.verbalService.parseWordpartAndPhonemeTokens(
        text?.text
      ).displayStr;
    }
  }

  parseAnswerChoices(popData: PopulationMultipleChoice): void {
    // add display text for each answer choice
    popData.answerChoices.forEach(choice => {
      const choiceText = choice?.choice?.texts?.[0];
      if (choiceText?.text) {
        choiceText.displayText =
          this.verbalService.parseWordpartAndPhonemeTokens(
            choiceText.text
          ).displayStr;
      }

      const choiceTextFeedback = choice.targetedFeedback?.firstIncorrect;
      if (choiceTextFeedback?.content) {
        choiceTextFeedback.displayText =
          this.verbalService.parseWordpartAndPhonemeTokens(
            choiceTextFeedback.content
          ).displayStr;
      }

      const choiceTextFeedback2 = choice.targetedFeedback?.secondIncorrect;
      if (choiceTextFeedback2?.content) {
        choiceTextFeedback2.displayText =
          this.verbalService.parseWordpartAndPhonemeTokens(
            choiceTextFeedback2.content
          ).displayStr;
      }
    });
  }

  parseSortingBuckets(popData: PopulationDragAndDrop): void {
    // add display text for each bucket label
    popData.labels?.forEach(label => {
      if (label.text) {
        label.displayText = this.verbalService.parseWordpartAndPhonemeTokens(
          label.text
        ).displayStr;
      }
    });
  }

  isPreK2ReadingActivity(): boolean {
    return this.userService.grade$.getValue() < 3 &&
      this.currActivity?.product === 'Reading' &&
      this.currActivity.targetText
      ? true
      : false;
  }
}
